/**
 * Placement Types - Unified Placement System
 *
 * Type definitions for design placement within print areas.
 * All units reference PrintArea-Space (not Canvas-Space). A placement that
 * is stored (`DesignPosition.placementMm`) is computed on the area's
 * millimetres: pass a context with the mm size as `widthPx`/`heightPx` and
 * `pxPerMm` 1, then `mm` units are millimetres and the result is mm (R44).
 */

import type { PrintContext } from '../print/context.js';

// ═══════════════════════════════════════════════════════════════════════════
// UNIT SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Unit type for placement values
 *
 * All units reference PrintArea-Space:
 * - relative: 0-1 fraction of PrintArea dimension
 * - mm: millimeters (converted via pxPerMm)
 * - px: pixels in PrintArea-Space
 */
export type Unit =
  | { type: 'relative'; value: number }
  | { type: 'mm'; value: number }
  | { type: 'px'; value: number };

/**
 * Helper to create a relative unit (0-1)
 */
export function relative(value: number): Unit {
  return { type: 'relative', value };
}

/**
 * Helper to create a millimeter unit
 */
export function mm(value: number): Unit {
  return { type: 'mm', value };
}

/**
 * Helper to create a pixel unit
 */
export function px(value: number): Unit {
  return { type: 'px', value };
}

// ═══════════════════════════════════════════════════════════════════════════
// PLACEMENT PRESET
// ═══════════════════════════════════════════════════════════════════════════

/**
 * External API dimensions for Mockup Factory
 *
 * Physical dimensions in millimeters for external mockup generation APIs.
 */
export interface ExternalApiDimensions {
  /** Design width in millimeters */
  designWidthMm: number;
  /** Design height in millimeters */
  designHeightMm: number;
  /** Distance from top edge in millimeters */
  designYTopMm: number;
  /** Horizontal offset from center in mm (0 = centered, negative = left) */
  designXCenterMm: number;
}

/**
 * PlacementPreset - Complete definition for design positioning
 *
 * Consolidates all placement information from:
 * - placements.config.ts (relative positions)
 * - placement.mapper.ts (physical mm dimensions)
 * - constants.ts (UI labels)
 */
export interface PlacementPreset {
  // ─────────────────────────────────────────────────────────────────────────
  // IDENTIFICATION & UI
  // ─────────────────────────────────────────────────────────────────────────

  /** Unique identifier (e.g., 'front_center_large') */
  id: string;
  /** Display name (e.g., 'Large Front Print') */
  name: string;
  /** Description (e.g., 'Centered, approx. 30×40 cm') */
  description: string;
  /** Optional icon for UI */
  icon?: string;

  // ─────────────────────────────────────────────────────────────────────────
  // CONTAIN BOX (Design fits within this area)
  // ─────────────────────────────────────────────────────────────────────────

  /** Maximum width for the design (contain box) */
  maxWidth: Unit;
  /** Maximum height for the design (contain box) */
  maxHeight: Unit;

  // ─────────────────────────────────────────────────────────────────────────
  // POSITION (Center of design in PrintArea-Space)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Horizontal position of design CENTER in PrintArea
   * - relative 0.5 = horizontally centered
   * - px 100 = 100px from left edge
   */
  positionX: Unit;

  /**
   * Vertical position of design CENTER in PrintArea
   * - relative 0.5 = vertically centered
   * - px 50 = 50px from top edge
   */
  positionY: Unit;

  // ─────────────────────────────────────────────────────────────────────────
  // EXTERNAL API (for Mockup Factory)
  // ─────────────────────────────────────────────────────────────────────────

  /** Physical dimensions for external mockup generation APIs */
  externalApi?: ExternalApiDimensions;

  // ─────────────────────────────────────────────────────────────────────────
  // OPTIONAL SETTINGS
  // ─────────────────────────────────────────────────────────────────────────

  /** Rotation in degrees (default: 0) */
  rotationDeg?: number;

  /**
   * Overflow behavior:
   * - 'clamp': Keep design within PrintArea bounds (default)
   * - 'allow': Allow design to extend beyond PrintArea
   */
  overflow?: 'allow' | 'clamp';

  /**
   * Maximum scale factor (default: Infinity)
   * Use to prevent excessive upscaling of raster images
   */
  maxScale?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT TYPES (Derived from PrintContext)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PlacementContext - Minimal context for placement calculation
 *
 * Only the fields needed by the calculator (no canvas offsets).
 */
export type PlacementContext = Pick<
  PrintContext,
  'widthPx' | 'heightPx' | 'pxPerMm'
>;

/**
 * CanvasContext - Extended context for canvas data generation
 *
 * Includes offsets for PrintArea-to-Canvas transformation.
 */
export type CanvasContext = Pick<
  PrintContext,
  'widthPx' | 'heightPx' | 'pxPerMm' | 'offsetX' | 'offsetY'
>;

// ═══════════════════════════════════════════════════════════════════════════
// PLACEMENT RESULT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PlacementResult - Output from placement calculator
 *
 * All values are in PrintArea-Space with origin='center' assumption.
 */
export interface PlacementResult {
  /** X position of design CENTER in PrintArea-Space */
  left: number;
  /** Y position of design CENTER in PrintArea-Space */
  top: number;
  /** Scale factor to apply uniformly (scaleX = scaleY) */
  scale: number;
  /** Rotation in degrees */
  rotationDeg: number;
  /** Final width after scaling */
  scaledWidth: number;
  /** Final height after scaling */
  scaledHeight: number;
}
